import {CLASS_NAMES} from '@github-ui/commenting/DomElements'
import {type Repository, useCurrentRepository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import {commitDiscussionsPath} from '@github-ui/paths'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useCSRFToken} from '@github-ui/use-csrf-token'
import {verifiedFetch, verifiedFetchJSON} from '@github-ui/verified-fetch'
import {BellIcon, BellSlashIcon, KeyIcon, LockIcon} from '@primer/octicons-react'
import {Box, Button, CounterLabel, Flash, Spinner} from '@primer/react'
import clsx from 'clsx'
import {useCallback, useEffect, useMemo, useState} from 'react'

import type {CommitComment, InitialCommentInfo} from '../../../types/commit-types'
import type {Commit} from '../../../types/shared'
import {isWeirichCommit} from '../../../utils/weirich-commit'
import styles from './Comment.module.css'
import {ExistingCommitComments} from './ExistingCommitComments'
import {LockConversationDialog} from './LockConversationDialog'
import {NewCommitComment} from './NewCommitComment'

// id to jump to when navigating to the comments section from commits list
export const COMMENTS_CONTAINER_ID = 'comments'

export function DiscussionComments({commit, commentInfo}: {commit: Commit; commentInfo: InitialCommentInfo}) {
  const repo = useCurrentRepository()
  const currentUser = useCurrentUser()
  const {
    comments,
    setComments,
    commentCount,
    setCommentCount,
    hasMoreComments,
    subscribed,
    loadDiscussionComments,
    loaded,
    didError,
  } = useDiscussionComments(commit, repo)

  // Used for quote replies
  const [newCommentContent, setNewCommentContent] = useState<string | undefined>(undefined)

  const isWeirichCommitValue = useMemo(() => isWeirichCommit(commit.oid, repo.id), [commit.oid, repo.id])

  const addComment = (comment: CommitComment) => {
    setComments([...comments, comment])
    setCommentCount((commentCount ?? 0) + 1)
  }

  const deleteComment = (comment: CommitComment) => {
    setComments(comments.filter(c => c.id !== comment.id))
    setCommentCount((commentCount ?? 0) - 1)
  }

  useEffect(() => {
    if (ssrSafeWindow?.location.hash) {
      const urlFragment = ssrSafeWindow.location.hash.slice(1)
      const comment = comments.find(c => c.urlFragment === urlFragment)

      if (comment) {
        const commentElement = document.getElementById(urlFragment)
        if (commentElement) {
          commentElement.scrollIntoView()
          commentElement.focus()
        }
      }
    }
  }, [comments])

  return (
    <Box
      className={clsx([
        'd-flex flex-column gap-3 pt-3',
        CLASS_NAMES.commentsContainer,
        styles['commit-discussion-comments'],
      ])}
    >
      <DiscussionCommentsHeader
        commitOid={commit.oid}
        commentCount={commentCount}
        canLock={commentInfo.canLock}
        locked={commentInfo.locked}
      />
      {!loaded ? (
        didError ? (
          <Flash className="d-flex flex-justify-between flex-items-center" variant="danger">
            <span>Failed to load comments.</span>
            <Button onClick={() => loadDiscussionComments()}>Retry</Button>
          </Flash>
        ) : (
          <div className="d-flex flex-justify-center">
            <Spinner size="large" />
          </div>
        )
      ) : hasMoreComments ? (
        <Button className="width-full" onClick={() => loadDiscussionComments(comments[0]?.id)}>
          Load more comments
        </Button>
      ) : null}
      <ExistingCommitComments
        comments={comments}
        commit={commit}
        deleteComment={deleteComment}
        setComments={setComments}
        setNewCommentContent={setNewCommentContent}
      />
      {isWeirichCommitValue ? (
        <div className="text-center">
          {[...Array(38)].map((_, i) => (
            <img
              key={`rose-${i}`}
              alt="rose"
              src="/images/icons/emoji/rose.png"
              className={styles['discussion-comments-rose']}
            />
          ))}
        </div>
      ) : null}
      {loaded || comments.length !== 0 ? (
        <>
          <NewCommitComment
            commitOid={commit.oid}
            addComment={addComment}
            newCommentContent={newCommentContent}
            canComment={commentInfo.canComment}
            locked={commentInfo.locked}
            repoArchived={commentInfo.repoArchived}
          />
          {currentUser ? <NotificationsFooter commitOid={commit.oid} subscribed={subscribed ?? false} /> : null}
        </>
      ) : null}
    </Box>
  )
}

function DiscussionCommentsHeader({
  commitOid,
  commentCount,
  locked,
  canLock,
}: {
  commitOid: string
  commentCount: number | undefined
  locked: boolean
  canLock: boolean
}) {
  const [lockDialogOpen, setLockDialogOpen] = useState(false)
  const [lockedSate, setLockedState] = useState(locked)
  return (
    <div className="d-flex flex-items-center flex-justify-between" id={COMMENTS_CONTAINER_ID}>
      <h2 className="sr-only">{commentCount} commit comments</h2>
      <div className="d-flex flex-items-center">
        <div className="h4 pr-2">Comments</div>
        {commentCount !== undefined && <CounterLabel>{commentCount}</CounterLabel>}
      </div>
      {canLock && (
        <>
          <Button
            leadingVisual={lockedSate ? KeyIcon : LockIcon}
            variant="invisible"
            onClick={() => setLockDialogOpen(true)}
          >
            {lockedSate ? 'Unlock' : 'Lock'} conversation
          </Button>
          {lockDialogOpen && (
            <LockConversationDialog
              commitOid={commitOid}
              locked={lockedSate}
              onClose={newLockedState => {
                setLockDialogOpen(false)
                if (newLockedState !== undefined) {
                  setLockedState(newLockedState)
                }
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

function NotificationsFooter({commitOid, subscribed}: {commitOid: string; subscribed: boolean}) {
  const [subscribeState, setSubscribeState] = useState(subscribed)
  const [updated, setUpdated] = useState(false)
  const repo = useCurrentRepository()
  const token = useCSRFToken('/notifications/thread', 'post')

  let descriptionText

  if (updated) {
    descriptionText = `You're now ${subscribeState ? 'subscribed' : 'unsubscribed'} to this thread.`
  } else {
    if (subscribeState) {
      descriptionText = "You're receiving notifications because you're subscribed to this thread."
    } else {
      descriptionText = "You're not receiving notifications from this thread."
    }
  }

  const updateSubscription = async () => {
    const formData = new FormData()
    formData.append('repository_id', repo.id.toString())
    formData.append('thread_id', commitOid)
    formData.append('thread_class', 'Commit')
    formData.append('id', subscribeState ? 'unsubscribe' : 'subscribe')
    // eslint-disable-next-line github/authenticity-token
    formData.append('authenticity_token', token ?? '')

    const response = await verifiedFetch('/notifications/thread', {
      method: 'POST',
      body: formData,
    })

    if (response.ok) {
      setSubscribeState(!subscribeState)
      setUpdated(true)
    }
  }

  return (
    <div className="d-flex flex-items-center border rounded-2 p-2 gap-2">
      <Button onClick={updateSubscription} size="small" leadingVisual={subscribeState ? BellSlashIcon : BellIcon}>
        {subscribeState ? 'Unsubscribe' : 'Subscribe'}
      </Button>
      <span className="f6 fgColor-muted">{descriptionText}</span>
    </div>
  )
}

interface DiscussionCommentData extends StaticDiscussionCommentData {
  comments: CommitComment[]
  commentCount: number
}

interface StaticDiscussionCommentData {
  canLoadMoreComments: boolean
  subscribed: boolean
}

function useDiscussionComments(commit: Commit, repo: Repository) {
  const [comments, setComments] = useState<CommitComment[]>([])
  const [loaded, setLoaded] = useState(false)
  const [staticData, setStaticData] = useState<StaticDiscussionCommentData | null>(null)
  const [commentCount, setCommentCount] = useState<number | undefined>(undefined)
  const [didError, setDidError] = useState(false)

  const loadDiscussionComments = useCallback(
    async (beforeCommentId?: string) => {
      setLoaded(false)
      setDidError(false)
      const response = await verifiedFetchJSON(
        commitDiscussionsPath({
          owner: repo.ownerLogin,
          repo: repo.name,
          commitOid: commit.oid,
          beforeCommentId,
        }),
      )

      if (response.ok) {
        const data: DiscussionCommentData = await response.json()
        if (beforeCommentId) {
          setComments(c => [...data.comments, ...c])
        } else {
          setComments(data.comments)
        }

        setStaticData({
          canLoadMoreComments: data.canLoadMoreComments,
          subscribed: data.subscribed,
        })
        setCommentCount(data.commentCount)
        setLoaded(true)
        setDidError(false)
      } else {
        setDidError(true)
      }
    },
    [repo.ownerLogin, repo.name, commit.oid],
  )

  useEffect(() => {
    if (!loaded) {
      loadDiscussionComments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDiscussionComments])

  return {
    comments,
    setComments,
    commentCount,
    setCommentCount,
    hasMoreComments: staticData?.canLoadMoreComments,
    subscribed: staticData?.subscribed,
    loadDiscussionComments,
    loaded,
    didError,
  }
}

try{ DiscussionComments.displayName ||= 'DiscussionComments' } catch {}
try{ DiscussionCommentsHeader.displayName ||= 'DiscussionCommentsHeader' } catch {}
try{ NotificationsFooter.displayName ||= 'NotificationsFooter' } catch {}